import * as XLSX from 'xlsx';
import { OrderHistory } from '../../stores/appOrderHistoryStore';
import { getTableFormattedDate, toDate } from '../../utils/dateUtils';
import { getOrderVatAmount } from '../../components/orderhistory/vatUtils';
import { buildOrderLineItemRows } from './orderLineItemsExport';

interface Sheet1Row {
  date: string;
  supplier: string;
  invoiceNumber: string;
  totalExVat: number;
  shipping: number;
  vatAmount: number;
  totalInclVat: number;
  vatPercentage: string;
}

/**
 * Calculates VAT amount and percentage for an order
 * VAT is calculated as: (totalWithTaxes - total) - shipping
 */
function calculateVATInfo(order: OrderHistory): { vatAmount: number; vatPercentage: string } {
  const totalIncVat = order.totalWithTaxes || order.total;
  const totalExVat = order.total;

  // Remove shipping from VAT calculation to match UI display
  const vatAmount = getOrderVatAmount(order.platform, totalIncVat, totalExVat, order.shipping ?? 0);

  // Calculate VAT percentage
  let vatPercentage = '0%';
  if (totalExVat > 0) {
    const vatPercent = (vatAmount / totalExVat) * 100;
    vatPercentage = `${vatPercent.toFixed(1)}%`;
  }

  return { vatAmount, vatPercentage };
}

/**
 * Generates and downloads an Excel file with order and product data
 *
 * @param orders - Filtered orders to include in the export
 * @param showExVat - Whether to show prices excluding VAT
 * @param filename - Name of the file to download (without extension)
 */
export function exportOrdersToExcel(
  orders: OrderHistory[],
  showExVat: boolean,
  filename: string = 'order-history-export'
): void {
  // Prepare Sheet 1: Orders Overview
  const sheet1Data: Sheet1Row[] = orders.map(order => {
    const { vatAmount, vatPercentage } = calculateVATInfo(order);

    // Use unknownSupplierName if platform is any_invoice, otherwise use platform
    const supplierName = order.platform === 'any_invoice' && order.unknownSupplierName
      ? order.unknownSupplierName
      : order.platform;

    return {
      date: getTableFormattedDate(toDate(order.orderDate)),
      supplier: supplierName,
      invoiceNumber: order.orderNumber || '',
      totalExVat: order.total,
      shipping: order.shipping ?? 0,
      vatAmount: vatAmount,
      totalInclVat: order.totalWithTaxes || order.total,
      vatPercentage: vatPercentage,
    };
  });

  // Prepare Sheet 2: Product Details. Same rows the Orders tab's "Copy line items" action copies;
  // the builder narrows them to the sheet's nine columns, so the file's shape is unchanged.
  const sheet2Data = buildOrderLineItemRows(orders, showExVat);

  // Create workbook
  const workbook = XLSX.utils.book_new();

  // Create Sheet 1
  const sheet1 = XLSX.utils.json_to_sheet(sheet1Data, {
    header: ['date', 'supplier', 'invoiceNumber', 'totalExVat', 'shipping', 'vatAmount', 'totalInclVat', 'vatPercentage'],
  });

  // Set column headers for Sheet 1
  XLSX.utils.sheet_add_aoa(sheet1, [
    ['Date', 'Supplier', 'Factuur Number', 'Total Ex VAT', 'Shipping', 'VAT Amount', 'Total Incl VAT', 'VAT %']
  ], { origin: 'A1' });

  // Create Sheet 2
  const sheet2 = XLSX.utils.json_to_sheet(sheet2Data, {
    header: ['productName', 'articleId', 'supplier', 'category', 'quantity', 'unitPrice', 'totalPrice', 'orderNumber', 'date'],
  });

  // Set column headers for Sheet 2
  XLSX.utils.sheet_add_aoa(sheet2, [
    ['Product Name', 'Article ID', 'Supplier', 'Category', 'Quantity', 'Unit Price', 'Total Price', 'Order Number', 'Date']
  ], { origin: 'A1' });

  // Set column widths for better readability
  sheet1['!cols'] = [
    { wch: 12 },  // Date
    { wch: 15 },  // Supplier
    { wch: 20 },  // Invoice Number
    { wch: 15 },  // Total Ex VAT
    { wch: 12 },  // Shipping
    { wch: 12 },  // VAT Amount
    { wch: 15 },  // Total Incl VAT
    { wch: 10 },  // VAT %
  ];

  sheet2['!cols'] = [
    { wch: 30 },  // Product Name
    { wch: 15 },  // Article ID
    { wch: 15 },  // Supplier
    { wch: 15 },  // Category
    { wch: 10 },  // Quantity
    { wch: 12 },  // Unit Price
    { wch: 12 },  // Total Price
    { wch: 20 },  // Order Number
    { wch: 12 },  // Date
  ];

  // Add sheets to workbook
  XLSX.utils.book_append_sheet(workbook, sheet1, 'Orders Overview');
  XLSX.utils.book_append_sheet(workbook, sheet2, 'Product Details');

  // Generate Excel file and trigger download
  XLSX.writeFile(workbook, `${filename}.xlsx`);
}
